import { getEntity, getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { isOpenGovAdmin, userSelector } from '@opengov/app-core/session/selectors';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/selectors';
import { FLAG_PRODUCT_STORIES_ACCESS_CONTROL } from '@opengov/stories-shared/constants';

import { storiesMsgs } from '../../locale';
import { EStoryLists } from '../../types';
import {
  selectIsPerformanceManagementFeatureFlagEnabled,
  selectIsFormAndWorkflowsFeatureFlagEnabled,
  selectStoriesPublicationFlag,
  selectIsCustomSpreadsheetReportEnabledFlag,
  selectStoriesOrganizationFlag,
  selectCopyStoriesFoldersFlag,
  selectCopyIndividualStoriesFlag,
  selectStoriesExportFolderPdfFlag,
  selectStoryMarginsEnabled,
  selectShowGlobalNavBar,
  selectShowFollowableStories
} from '../common/flags';
import { IStoryListProps } from '../components/StoryList';
import {
  navigateToCreateFromTemplate,
  navigateToSettings,
  navigateToStoryView
} from '../state';
import {
  navigateToFolderList,
  navigateToOrganizeStoriesAndFolders,
  navigateToPublicationList,
  navigateToPublicationView,
  navigateToStoryList,
  navigateToTemplateList
} from '../state/navigation';
import { selectIsTemplate, selectLastPath } from './storiesSelectors';

export const selectNavigateToStoryViewProps = createStructuredSelector<any, any>({
  entitySubdomain: getEntitySubdomain
});

export const selectNavigateToSettingsProps = createStructuredSelector<any, any>({
  entitySubdomain: getEntitySubdomain
});

export {
  selectStoriesOrganizationFlag,
  selectCopyStoriesFoldersFlag,
  selectCopyIndividualStoriesFlag,
  selectStoriesExportFolderPdfFlag,
  selectStoryMarginsEnabled,
  selectShowGlobalNavBar,
  selectShowFollowableStories
} from '../common/flags';

export const selectStoryListProps = createStructuredSelector<any, IStoryListProps>({
  showGlobalNavBar: selectShowGlobalNavBar,
  storyMarginsEnabled: selectStoryMarginsEnabled,
  canCopyIndividualStories: selectCopyIndividualStoriesFlag,
  canCopyStoriesFolders: selectCopyStoriesFoldersFlag,
  canOrganizeStories: selectStoriesOrganizationFlag,
  canFollowStories: selectShowFollowableStories,
  storiesAccessControlEnabled: createSelector(selectFeatureFlags, (flags) => flags.get(FLAG_PRODUCT_STORIES_ACCESS_CONTROL)),
  isTemplate: selectIsTemplate,
  user: userSelector,
  isOpenGovAdmin,
  canOrganizeFolders: selectStoriesExportFolderPdfFlag,
  lastPath: selectLastPath,
  entityId: getNumericalEntityId,
  entityName: createSelector(getEntity, (entity) => entity.display_name),
  entityUuid: createSelector(getEntity, (entity) => entity.uuid),
  entitySubdomain: createSelector(getEntitySubdomain, (entitySubdomain) => entitySubdomain),
  publicationViewMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.publicationView })),
  messages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.storyList })),
  publicationsMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.publicationList })),
  publicationModalMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.publicationModals })),
  modalMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.modals })),
  displaySidebar: selectStoriesOrganizationFlag,
  displayPublicationTab: selectStoriesPublicationFlag,
  isFormsAndWorkflowsFeatureEnabled: selectIsFormAndWorkflowsFeatureFlagEnabled,
  isPerformanceManagementFeatureEnabled: selectIsPerformanceManagementFeatureFlagEnabled,
  isCustomSpreadsheetReportEnabled: selectIsCustomSpreadsheetReportEnabledFlag,
  pushToOrganizeStoriesAndFolders: () => (entitySubdomain, selectedFolder, rootId) => {
    navigateToOrganizeStoriesAndFolders(entitySubdomain, selectedFolder, rootId);
  },
  setSelectedList:
    () =>
      (newList, entitySubdomain, isOrganize = false, rootId) => {
        if (entitySubdomain) {
          switch (newList.id) {
            case EStoryLists.Stories:
              navigateToStoryList(entitySubdomain);
              break;
            case EStoryLists.Templates:
              navigateToTemplateList(entitySubdomain);
              break;
            case EStoryLists.Publications:
              navigateToPublicationList(entitySubdomain);
              break;
            default:
              isOrganize
                ? navigateToOrganizeStoriesAndFolders(entitySubdomain, newList.id, rootId)
                : navigateToFolderList(entitySubdomain, newList.id);
          }
        }
      },
  navigateToStoryView: () => (entitySubdomain, storyId, isTemplate) =>
    navigateToStoryView(entitySubdomain, storyId, isTemplate),
  navigateToPublicationView: () => (entitySubdomain, publicationId) =>
    navigateToPublicationView(entitySubdomain, publicationId),
  navigateToCreateFromTemplate: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => () => navigateToCreateFromTemplate(entitySubdomain)
  ),
  navigateToSettings: createSelector(
    selectNavigateToSettingsProps,
    ({ entitySubdomain }) => (tab) => navigateToSettings(entitySubdomain, tab)
  ),
});
